import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { GalacticTraderOffer, GameBoard, ResourceType } from '@prometheus/types'
import { useSelector } from '../../../hooks/useSelector'
import { pushError } from '../../../reducers/game/game'
import usePlayerActions from './usePlayerActions'
import {
  ReadyRequirement,
  pickAutoTravel,
  readyRequirementSignature,
  resolveReadyRequirement,
} from '../readyGate'

const OFFER_RESOURCES: ResourceType[] = ['Wa', 'Cu', 'Fe', 'Ag', 'Au', 'Oil', 'Dia', 'Ti', 'Pt', 'U']

const EMPTY_OFFER: GalacticTraderOffer = {
  Wa: { quantity: 0, price: 0 },
  Cu: { quantity: 0, price: 0 },
  Fe: { quantity: 0, price: 0 },
  Ag: { quantity: 0, price: 0 },
  Au: { quantity: 0, price: 0 },
  Oil: { quantity: 0, price: 0 },
  Dia: { quantity: 0, price: 0 },
  Ti: { quantity: 0, price: 0 },
  Pt: { quantity: 0, price: 0 },
  U: { quantity: 0, price: 0 },
}

export interface ReadyGate {
  /** The live requirement, or null when Ready can just fire. */
  requirement: ReadyRequirement | null
  /** The options panel is showing (one tap in). */
  armed: boolean
  /** A call is in flight — the button and the panel controls are inert. */
  busy: boolean
  /** The Ready button's onClick: ready up, arm, or decide. */
  onReadyTap: () => Promise<void>
  /** Travel to a specific destination from the panel. */
  onChooseTravel: (planet: string) => Promise<void>
  /** Take the requirement's own decision (same as a second Ready tap). */
  onAuto: () => Promise<void>
  /** Close the panel without deciding anything. */
  onDismiss: () => void
}

/**
 * One Ready button, three jobs.
 *
 * Tap with nothing outstanding and it readies up, exactly as before. Tap while
 * the game still wants a travel choice or a trade answer and the FIRST tap
 * opens the options under the button instead of erroring; the SECOND tap hands
 * the decision to the game — a random destination (what `forceTurnEnd` does
 * when the timer beats the player to it) or the saved trade offer committed
 * as-is. That is the whole point: a player who does not care can keep tapping
 * the same spot and the turn keeps moving, which on a phone saves crossing
 * two tabs to reach a planet card or the trader.
 *
 * Committing the SAVED offer rather than a blank one is deliberate — a bid the
 * player typed and the trader auto-saved should not be thrown away by a second
 * tap. With no bids in, committing the saved (empty) offer IS skipping, which
 * is what the panel then calls it.
 */
export default function useReadyGate(): ReadyGate {
  const dispatch = useDispatch()
  const uid = useSelector(s => s.auth.uid) || ''
  const turn = useSelector(s => s.game.metadata?.turn) || 0
  const players = useSelector(s => s.game.metadata?.players) || {}
  const playersChoosingPlanet = useSelector(s => s.game.metadata?.players_choosing_planet) || []
  const trade = useSelector(s => s.game.metadata?.trade)
  const movementOptions = useSelector(s => s.game.sheet?.planet_movement_options)
  const savedOffer = useSelector(s => s.game.sheet?.trade_offer)
  const { onReady, onPickChoosablePlanet, onCommitTrade } = usePlayerActions()

  const [armed, setArmed] = useState(false)
  const [busy, setBusy] = useState(false)
  // Guards the async gap: an unmounted panel must not setState, and the
  // Ready FAB is tappable again the instant a call resolves. The flag is set
  // in the effect BODY, not at ref creation — under StrictMode (or any effect
  // remount) the cleanup runs once without a real unmount, and a flag that
  // never re-arms would leave the button stuck busy forever.
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])

  const playerNames = useMemo(() => {
    const names: Record<string, string> = {}
    Object.values(players).forEach(p => { names[p.id] = p.name })
    return names
  }, [players])

  const movementKeys = useMemo(() => Object.keys(movementOptions || {}), [movementOptions])
  // Same source every other travel control reads: on decennial rift years the
  // client forbids landing on any planet a ship is parked on.
  const occupiedPlanets = useMemo(() => Object.values(players).map(p => p.current_planet), [players])
  // "Empty" is about quantities, not about the offer existing: the trader
  // auto-saves prices the moment the window opens, so a saved offer with no
  // quantities on it is still a player who has bid nothing.
  const offerIsEmpty = useMemo(
    () => !savedOffer || OFFER_RESOURCES.every(r => !savedOffer[r]?.quantity),
    [savedOffer],
  )

  const requirement = useMemo(() => resolveReadyRequirement({
    uid,
    dead: !!players[uid]?.dead,
    turn,
    playersChoosingPlanet,
    playerNames,
    movementOptions: movementKeys,
    occupiedPlanets,
    tradeOpen: !!trade,
    tradeCommitted: !!savedOffer?.committed,
    tradeOfferEmpty: offerIsEmpty,
  }), [uid, players, turn, playersChoosingPlanet, playerNames, movementKeys, occupiedPlanets, trade, savedOffer, offerIsEmpty])

  // Close the panel whenever the phase it was describing ends — the choice
  // landed, the offer went in, the turn processed. Keyed on the requirement
  // KIND, so a rift queue advancing under the player does not slam the panel
  // shut mid-read.
  const signature = readyRequirementSignature(requirement)
  useEffect(() => { setArmed(false) }, [signature])

  const run = useCallback(async (invoke: () => Promise<any>) => {
    setBusy(true)
    try {
      await invoke()
      if (mounted.current) {
        setArmed(false)
      }
    } catch (e) {
      dispatch(pushError({ type: 'error', message: (e as any).message }))
    } finally {
      if (mounted.current) {
        setBusy(false)
      }
    }
  }, [dispatch])

  const onChooseTravel = useCallback((planet: string) => (
    run(() => onPickChoosablePlanet(planet as keyof GameBoard))
  ), [run, onPickChoosablePlanet])

  const onResolveTrade = useCallback(() => (
    // The engine reads only `committed` off this payload and commits whatever
    // it already has saved for the player — same call the trader's own
    // "Skip Offer" / "Commit Prices" button makes.
    run(() => onCommitTrade({ ...(savedOffer || EMPTY_OFFER), committed: true }))
  ), [run, onCommitTrade, savedOffer])

  const onAuto = useCallback(async () => {
    if (!requirement || busy) {
      return
    }
    if (requirement.kind === 'travel') {
      const choice = pickAutoTravel(requirement.choices)
      if (choice) {
        await onChooseTravel(choice.key)
      }
      return
    }
    if (requirement.kind === 'trade') {
      await onResolveTrade()
    }
    // 'waiting-*' has nothing this player can decide — the panel stays up and
    // says who is being waited on rather than firing a call that would throw.
  }, [requirement, busy, onChooseTravel, onResolveTrade])

  const onReadyTap = useCallback(async () => {
    if (busy) {
      return
    }
    if (!requirement) {
      setArmed(false)
      await run(() => onReady())
      return
    }
    if (!armed) {
      setArmed(true)
      return
    }
    await onAuto()
  }, [busy, requirement, armed, run, onReady, onAuto])

  const onDismiss = useCallback(() => setArmed(false), [])

  return { requirement, armed, busy, onReadyTap, onChooseTravel, onAuto, onDismiss }
}
